import { useState, useEffect, useMemo } from 'react';
import { AppBar, Toolbar, Box, IconButton, Drawer, List, ListItem, Stack, ButtonBase } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import HeaderButton from './HeaderButton';
import LanguageToggle from './LanguageToggle';
import useActiveSection from './useActiveSection';
import { tokens } from '../theme';

type HeaderProps = {
  page: 'landing' | 'menu' | 'partners';
};

// Every anchored section is observed, including `reviews-section`, which has no
// nav item of its own. Leaving it out made the spy hold the previous section's
// highlight while the visitor was actually reading reviews — the nav claiming a
// position the page was not in.
const LANDING_SECTIONS = [
  'hero-section',
  'about-section',
  'menu-preview-section',
  'location-section',
  'reviews-section',
  'footer-section',
];

const Header = ({ page }: HeaderProps) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isLanding = page === 'landing';
  const sectionIds = useMemo(() => LANDING_SECTIONS, []);
  const activeSection = useActiveSection(sectionIds, isLanding);

  useEffect(() => {
    if (!isLanding) return;

    const handleScroll = () => setScrolled(window.scrollY > 24);
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isLanding]);

  // Sub-pages get the solid treatment immediately; there is no hero for the bar
  // to float over, so transparency would just look unfinished.
  const solid = !isLanding || scrolled;

  const links = [
    { key: 'home', section: 'hero-section' },
    { key: 'about', section: 'about-section' },
    { key: 'menu', section: 'menu-preview-section' },
    { key: 'location', section: 'location-section' },
    { key: 'contact', section: 'footer-section' },
  ].filter((link) => isLanding || link.key === 'home');

  const goHome = () => {
    if (isLanding) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <AppBar
      // Sticky on every page. The menu page previously used `relative`, which
      // left a long scrolling menu with no way back to navigation.
      position="sticky"
      elevation={0}
      sx={{
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        color: 'text.primary',
        backgroundColor: solid ? 'rgba(255, 255, 255, 0.82)' : 'transparent',
        backdropFilter: solid ? 'saturate(180%) blur(12px)' : 'none',
        WebkitBackdropFilter: solid ? 'saturate(180%) blur(12px)' : 'none',
        borderBottom: '1px solid',
        borderColor: solid ? tokens.color.line : 'transparent',
        boxShadow: solid ? tokens.shadow.sm : 'none',
        // Named properties only. `transition: all` also animates layout and
        // paint properties, which is what made this bar feel heavy.
        transition: [
          `background-color ${tokens.duration.surface} ease`,
          `border-color ${tokens.duration.surface} ease`,
          `box-shadow ${tokens.duration.surface} ease`,
        ].join(', '),
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: { xs: tokens.headerHeight.xs, md: tokens.headerHeight.md },
          height: { xs: tokens.headerHeight.xs, md: tokens.headerHeight.md },
          px: { xs: 2, md: 5 },
          gap: 2,
        }}
      >
        {/*
          A real button rather than an <img> carrying role="button" and a hand-
          rolled keydown handler — Enter, Space and the focus ring all come for
          free, and the alt text becomes the button's accessible name.
        */}
        <ButtonBase
          onClick={goHome}
          aria-label={(t as any)('links.home')}
          sx={{
            borderRadius: `${tokens.radius.sm}px`,
            p: 0.5,
            transition: `transform ${tokens.duration.press} ${tokens.ease.out}`,
            '&:active': { transform: 'scale(0.96)' },
          }}
        >
          <Box
            component="img"
            src={`${process.env.REACT_APP_CLOUDFRONT_URL}/public/NavBarTokki.png`}
            alt="Tokki Creperie"
            fetchPriority="high"
            decoding="async"
            sx={{
              // Was 100px tall, which ate most of the header on mobile.
              height: { xs: 38, md: 50 },
              width: 'auto',
              display: 'block',
            }}
          />
        </ButtonBase>

        <Box sx={{ flexGrow: 1 }} />

        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ display: { xs: 'none', md: 'flex' } }}
        >
          {links.map((link) => (
            <HeaderButton
              key={link.key}
              text={(t as any)(`links.${link.key}`)}
              sectionId={link.section}
              page={page}
              active={isLanding && activeSection === link.section}
            />
          ))}
          {page !== 'partners' && (
            <Box sx={{ ml: 1 }}>
              <LanguageToggle />
            </Box>
          )}
        </Stack>

        <IconButton
          edge="end"
          color="inherit"
          aria-label={(t as any)('links.menu')}
          aria-expanded={mobileMenuOpen}
          onClick={() => setMobileMenuOpen(true)}
          sx={{ display: { xs: 'inline-flex', md: 'none' } }}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>

      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{ display: { xs: 'block', md: 'none' } }}
        PaperProps={{
          sx: {
            width: 288,
            bgcolor: tokens.color.cream,
            px: 1.5,
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 1.5, height: tokens.headerHeight.xs }}
        >
          <Box
            component="img"
            src={`${process.env.REACT_APP_CLOUDFRONT_URL}/public/NavBarBunny.png`}
            alt="Tokki"
            loading="lazy"
            decoding="async"
            sx={{ height: 38, width: 'auto' }}
          />
          <IconButton onClick={() => setMobileMenuOpen(false)} aria-label="Cerrar">
            <CloseIcon />
          </IconButton>
        </Stack>

        <List sx={{ pt: 1 }}>
          {links.map((link) => (
            <ListItem key={link.key} disablePadding sx={{ mb: 0.5 }}>
              <Box sx={{ width: '100%' }} onClick={() => setMobileMenuOpen(false)}>
                <HeaderButton
                  text={(t as any)(`links.${link.key}`)}
                  sectionId={link.section}
                  page={page}
                  active={isLanding && activeSection === link.section}
                  fullWidth
                />
              </Box>
            </ListItem>
          ))}
          <ListItem sx={{ mt: 1.5, px: 1.75 }}>
            <LanguageToggle />
          </ListItem>
        </List>
      </Drawer>
    </AppBar>
  );
}

export default Header;
